import * as React from "react";
import Box from "@mui/material/Box";
import clsx from "clsx";
import css from "../Toolbar/Toolbar.module.css";
import {SearchInput} from "../Toolbar/SearchInput.tsx";
import dayjs, {Dayjs} from "dayjs";
import {Collapse, IconButton, Popover} from "@mui/material";
import {JSX, useCallback, useEffect, useState} from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {TwoLineBtn} from "../../Components/Button/TwoLineBtn.tsx";
import {DEF} from "../../const.ts";
import {DateRange} from "../../Components/Date/DateRange.tsx";
import Profile from "../Model/Profile.ts";
import LeadManagementApi from "./LeadManagementApi.ts";
import LeadFilter from "../Model/LeadFilter.ts";
import LeadStatus from "../Model/LeadStatus.ts";

export interface LeadToolbarProps {
    token: string
    profile: Profile,
    leadFilter: LeadFilter,
    setLeadFilter: React.Dispatch<React.SetStateAction<LeadFilter>>,
    filterReady: boolean,
    setFilterReady: React.Dispatch<React.SetStateAction<boolean>>,
}

const LeadToolbar: React.FC<LeadToolbarProps> = (props):JSX.Element => {

    const [leadStatus, setLeadStatus] = React.useState<LeadStatus>();

    const [popoverContent, setPopoverContent] = React.useState<JSX.Element | null>(<></>);
    const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const [popoverClose, setPopoverClose] = React.useState<() => void>(() => () => {});
    const [canClosePopover, setCanClosePopover] = React.useState<boolean>(true);

    //-----------------------------------------------------------
    // Główne inicjacje
    useEffect(():void => {
        const fetchStatuses = async ():Promise<void> => {
            const api = new LeadManagementApi(props.token, props.profile);
            try {
                const leadStatuses = await api.leadStatusTypes();
                setLeadStatus({...leadStatuses});
            } catch (e) {
                console.error(e);
            }
        }

        fetchStatuses();
    }, []);
    //
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // epid
    const [epid, setEpid] = React.useState<string>('');
    const epidInputDraw = () : JSX.Element => {
        return (
            <>
                <SearchInput className={clsx(css.inputText, css.inputEpid,  css.border)}
                             placeholder="EPID"
                             value={epid}
                             setValue={setEpid}/>
            </>
        )
    }
    useEffect(() => {
        props.leadFilter.epid = (((epid) => epid || null )(epid.trim()));
        props.setFilterReady(true);
    }, [epid]);
    // epid
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // dateCreate
    const [dateCreateFrom, setDateCreateFrom] = React.useState<Dayjs>(
        props.leadFilter.dateCreateFrom
        ?? dayjs().subtract(3, 'month').startOf('month')
    );
    const [dateCreateTo, setDateCreateTo] = React.useState<Dayjs>(
        props.leadFilter.dateCreateTo
        ?? dayjs().subtract(1, 'day').endOf('day')
    );
    const [dateCreateTopRow, setDateCreateTopRow] = React.useState<React.JSX.Element>(<></>);
    const [dateCreateSuccess, setDateCreateSuccess] = React.useState<boolean>(false);
    const dateCreateBtnDraw = () : JSX.Element => {
        return (
            <>
                <TwoLineBtn className={clsx(css.creationDate)}
                            topRow={dateCreateTopRow}
                            bottomRow={'Data tworzenia'}
                            onClick={dateCreateBtnClick}/>
            </>
        )
    }
    const dateCreateBtnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setPopoverAnchorEl(event.currentTarget);
        setCanClosePopover(true);

        setPopoverContent((): JSX.Element => {
            return (
                <>
                    <DateRange dateFrom={dateCreateFrom}
                               setDateFrom={setDateCreateFrom}
                               dateTo={dateCreateTo}
                               setDateTo={setDateCreateTo}
                               setStatus={setDateCreateSuccess}
                    />
                </>
            )
        })
    }
    useEffect(() : void => {
        const dateCreateTopRowContent = ((): JSX.Element => {
            return (
                <Box sx={{display: 'flex', flexDirection: 'column', lineHeight: 1.0, fontSize:"14px"}}>
                    <span>Od: {dateCreateFrom.format(DEF.DATE_FORMAT)}</span>
                    <span>Do: {dateCreateTo.format(DEF.DATE_FORMAT)}</span>
                </Box>
            );
        })();

        setDateCreateTopRow(dateCreateTopRowContent);
    }, [dateCreateFrom, dateCreateTo]);
    useEffect(() => {
        setCanClosePopover(dateCreateSuccess)
        if (dateCreateSuccess) {
        }
        props.setFilterReady(true);
    }, [dateCreateSuccess]);
    // dateCreate
    //---------------------------------------------------------

    //-----------------------------------------------------------
    // installationAddress
    const [installationAddress, setInstallationAddress] = React.useState<string>('');
    const installationAddressDraw = () : JSX.Element => {
        return (
            <>
                <SearchInput className={clsx(css.textInput, css.border)}
                             placeholder="Adres instalacji"
                             value={installationAddress}
                             setValue={setInstallationAddress}/>
            </>
        )
    }
    useEffect(() => {
        props.leadFilter.installationAddress = ((installationAddress) => installationAddress || null)(installationAddress.trim());
        props.setFilterReady(true);
    }, [installationAddress]);
    // installationAddress
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // completionDate
    const [updateDateFrom, setUpdateDateFrom] = React.useState<Dayjs>(
        props.leadFilter.dateUpdateFrom
        ?? dayjs().subtract(3, 'month').startOf('month')
    );
    const [updateDateTo,   setUpdateDateTo] = React.useState<Dayjs>(
        props.leadFilter.dateUpdateTo
        ?? dayjs()
    );
    const [updateDateTopRow, setUpdateDateTopRow] = React.useState<React.JSX.Element>(<></>);
    const [updateDateSuccess, setUpdateDateSuccess] = React.useState<boolean>(false);
    const leadTimeBtnDraw = () : JSX.Element => {
        return (
            <>
                <TwoLineBtn className={clsx(css.date)}
                            topRow={updateDateTopRow}
                            bottomRow={'Data realizacji'}
                            onClick={completionDateBtnClick}/>
            </>
        )
    }
    const completionDateBtnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setPopoverAnchorEl(event.currentTarget);
        setCanClosePopover(true);
        setPopoverContent((): JSX.Element => {
            return (
                <>
                    <DateRange dateFrom={updateDateFrom}
                               setDateFrom={setUpdateDateFrom}
                               dateTo={updateDateTo}
                               setDateTo={setUpdateDateTo}
                               setStatus={setUpdateDateSuccess}
                    />
                </>
            )
        })
    }
    useEffect(() : void => {
        const leadTimeTopRowContent = ((): JSX.Element => {
            return (
                <Box sx={{display: 'flex', flexDirection: 'column', lineHeight: 1.0, fontSize:"14px"}}>
                    <span>Od: {updateDateFrom.format(DEF.DATE_FORMAT)}</span>
                    <span>Do: {updateDateTo.format(DEF.DATE_FORMAT)}</span>
                </Box>
            );
        })();

        setUpdateDateTopRow(leadTimeTopRowContent);
    }, [updateDateFrom, updateDateTo]);
    useEffect(() => {
        setCanClosePopover(updateDateSuccess);
        props.setFilterReady(true);
        if (dateCreateSuccess) {
        }
    }, [updateDateSuccess]);
    // completionDate
    //-----------------------------------------------------------


    //-----------------------------------------------------------
    // status
    const [selectedStatus, setSelectedStatus] = React.useState<string|null>(null);
    const selectHandleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value;
        setSelectedStatus(newValue);

        // Aktualizacja filtra w komponencie nadrzędnym (StatefulLeadToolbar)
        props.setLeadFilter(prev => ({
            ...prev,
            installationAddress: ((installationAddress) =>  installationAddress || null)(installationAddress.trim()),
            epid: ((epid) => epid || null)(epid.trim()),
            status: newValue
        }));
    };
    const leadStatusDraw = (): JSX.Element => {
        return (
            <Box sx={{minWidth: 200}}>
                <select className={clsx(css.select, css.border)}
                        aria-label="Status leada"
                        value={selectedStatus||''}
                        onChange={selectHandleChange}
                >
                    <option value=''>Wybierz status</option>
                    {leadStatus && Object.entries(leadStatus).map(([key, value]) => (
                        <option key={key} value={key}>
                            {value}
                        </option>
                    ))}
                </select>
            </Box>
        );
    };
    // status
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // Obsługa filtra

    useEffect(() => {
        const isDataValid = () => {
            // if (!dateCreateFrom || !dateCreateTo) return false;
            // if (epid && epid.length < 3) return false;
            // if (installationAddress && installationAddress.length < 3) return false;
            return true;
        };

        if (isDataValid()) {

            props.setLeadFilter({
                // ...props.leadFilter,
                dateCreateFrom: dateCreateFrom,
                dateCreateTo: dateCreateTo,
                dateUpdateFrom: updateDateFrom,
                dateUpdateTo: updateDateTo,
                status: selectedStatus,
                installationAddress: ((installationAddress) => installationAddress || null)(installationAddress.trim()),
                epid: ((epid) => epid || null)(epid.trim()),
            });
            console.log(props.leadFilter);
            props.setFilterReady(true);
        } else {
            props.setFilterReady(false);
        }
    }, [dateCreateFrom, dateCreateTo, updateDateFrom, updateDateTo, installationAddress, selectedStatus, epid]);

    // Obsługa filtra
    //-----------------------------------------------------------


    // @ts-ignore
    const handleClose = useCallback((event, reason) => {

        if (!canClosePopover) {
            return;
        }

        popoverClose();
        setPopoverClose(() => () => {});

        setPopoverAnchorEl(null);
        setPopoverContent(null);
    }, [canClosePopover]);


    const open = Boolean(popoverAnchorEl)
    const [expanded, setExpanded] = useState(false);

    const togglePanel = () => setExpanded(!expanded);

    const buttonsOnToolbar = () : JSX.Element => {
        return (
            <Box className={clsx(css.controls)}>

                <IconButton onClick={togglePanel} color="primary" size="small">
                    {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                </IconButton>

                <Box className={clsx(css.main)}>

                    {epidInputDraw()}
                    {dateCreateBtnDraw()}
                    {installationAddressDraw()}
                    {leadTimeBtnDraw()}
                    {leadStatusDraw()}

                </Box>

                <IconButton onClick={togglePanel} color="primary" size="small">
                    {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                </IconButton>
            </Box>


        )
    }

    const collapse = (): React.JSX.Element => {
        return (

            <Box className={clsx(css.collapse)}>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box className={clsx(css.content)}>
                        <Box>Dodatkowe filtry daty...</Box>
                        <Box>Wybór kategorii...</Box>
                        <Box>Opcje zaawansowane...</Box>
                    </Box>
                </Collapse>
            </Box>

        )
    }

    const popover = (): React.JSX.Element => {
        return (

            <Popover className={clsx(css.popover)}
                open={open}
                anchorEl={popoverAnchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        className: clsx(css.leadToolbar_popover_slot)
                    }
                }}
            >

                <Box  className={clsx(css.content)}>
                    {popoverContent}
                </Box>
            </Popover>

        )
    }

    return (

        <Box className={clsx(css.toolBar)}>

            {buttonsOnToolbar()}

            {collapse()}

            {popover()}

        </Box>
    )
}
export default LeadToolbar